import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { useInView } from 'framer-motion';

interface StatDef {
  prefix: string;
  target: number;
  suffix: string;
  label: string;
  decimals?: number;
}

const STATS: StatDef[] = [
  { prefix: '+', target: 20, suffix: '', label: 'Eventos realizados' },
  { prefix: '', target: 95, suffix: '%', label: 'Clientes satisfechos' },
  { prefix: '', target: 3, suffix: '', label: 'Formatos de catering' },
];

function easeOutQuart(t: number): number {
  return 1 - Math.pow(1 - t, 4);
}

function useCountUp(target: number, duration: number, active: boolean) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) return;
    let startTime: number | null = null;

    const tick = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setValue(Math.round(easeOutQuart(progress) * target));
      if (progress < 1) requestAnimationFrame(tick);
    };

    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);

  return value;
}

const StatCounter: React.FC<StatDef & { active: boolean; index: number }> = ({
  prefix,
  target,
  suffix,
  label,
  active,
  index,
}) => {
  const count = useCountUp(target, 1800 + index * 200, active);

  return (
    <Box sx={{ textAlign: 'center', py: 3 }}>
      <Typography
        component="div"
        sx={{
          fontFamily: 'var(--font-heading)',
          fontSize: { xs: '3.5rem', md: '4.5rem' },
          fontWeight: 700,
          color: 'var(--color-accent)',
          lineHeight: 1,
          mb: 1,
          letterSpacing: '-0.02em',
        }}
      >
        {prefix}{count}{suffix}
      </Typography>
      <Typography
        sx={{
          color: 'var(--color-text-secondary)',
          fontSize: '0.9rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const Stats: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <Box
      ref={ref}
      sx={{
        py: 8,
        background: 'linear-gradient(135deg, #1a1008, #0d0d0d)',
        borderTop: '1px solid var(--color-border)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
          {STATS.map((s, i) => (
            <Grid key={i} size={{ xs: 12, sm: 4 }}>
              <StatCounter {...s} active={isInView} index={i} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Stats;
